import * as React from 'react';
import styled from 'styled-components';
import { LoginState } from '../Login';
import { Icon } from '../../../shared/Icon';

/**
 * Represents a single step in the login progress stepper
 */
export interface ILoginStep {
  label: string;
  iconName: string;
  state: LoginState;
}

/**
 * Props for the LoginStepper component
 */
export interface ILoginStepperProps {
  currentState: LoginState;
  hasTenants: boolean;
  hasMultipleProviders: boolean;
}

/**
 * LoginStepper component - Displays a horizontal progress indicator for the login flow
 * Shows conditional steps based on user's authentication requirements
 */
export const LoginStepper: React.FC<ILoginStepperProps> = ({
  currentState,
  hasTenants,
  hasMultipleProviders,
}) => {
  // Hide stepper on email identification step and forgot password flow (matches mobile UI)
  if (currentState === LoginState.IDENTIFY || currentState === LoginState.FORGOT_PASSWORD) {
    return null;
  }

  /**
   * Build the list of steps dynamically based on the user's flow
   */
  const steps = React.useMemo((): ILoginStep[] => {
    const stepList: ILoginStep[] = [
      {
        label: 'Email',
        iconName: 'email',
        state: LoginState.IDENTIFY,
      },
    ];

    // Add tenant selection step if user has multiple tenants
    if (hasTenants) {
      stepList.push({
        label: 'Organization',
        iconName: 'business',
        state: LoginState.SELECT_TENANT,
      });
    }

    // Add provider selection step if user has multiple sign-in providers
    if (hasMultipleProviders) {
      stepList.push({
        label: 'Provider',
        iconName: 'vpn_key',
        state: LoginState.SELECT_PROVIDER,
      });
    }

    // Final step - authentication
    stepList.push({
      label: 'Sign In',
      iconName: 'login',
      state: LoginState.AUTHENTICATE,
    });

    return stepList;
  }, [hasTenants, hasMultipleProviders]);

  /**
   * Determine the current step index based on login state
   */
  const currentStepIndex = React.useMemo((): number => {
    // Find the index of the current step
    const index = steps.findIndex((step) => {
      switch (currentState) {
        case LoginState.SELECT_TENANT:
          return step.state === LoginState.SELECT_TENANT;
        case LoginState.SELECT_PROVIDER:
        case LoginState.SSO_AUTH:
          return step.state === LoginState.SELECT_PROVIDER;
        case LoginState.AUTHENTICATE:
        case LoginState.SECOND_FACTOR_REQUIRED:
        case LoginState.SECOND_FACTOR_VERIFICATION:
        case LoginState.SELECT_ORGANIZATION:
          // All authentication-related states map to the final "Sign In" step
          return step.state === LoginState.AUTHENTICATE;
        default:
          return false;
      }
    });

    return index !== -1 ? index : 0;
  }, [currentState, steps]);

  return (
    <StepperContainer>
      {steps.map((step, index) => (
        <React.Fragment key={step.state}>
          <StepCircle
            step={step}
            stepNumber={index + 1}
            isCurrent={index === currentStepIndex}
            isCompleted={index < currentStepIndex}
          />
          {index < steps.length - 1 && (
            <ConnectorLine isCompleted={index < currentStepIndex} />
          )}
        </React.Fragment>
      ))}
    </StepperContainer>
  );
};

/**
 * Props for the StepCircle component
 */
interface IStepCircleProps {
  step: ILoginStep;
  stepNumber: number;
  isCurrent: boolean;
  isCompleted: boolean;
}

/**
 * StepCircle component - Renders a single step in the progress indicator
 */
const StepCircle: React.FC<IStepCircleProps> = ({
  step,
  isCurrent,
  isCompleted,
}) => {
  const iconColor = isCurrent ? 'white' : isCompleted ? 'white' : undefined;

  return (
    <StepContainer>
      <Circle $isCurrent={isCurrent} $isCompleted={isCompleted}>
        {isCompleted ? (
          <Icon name="check" color="white" size="small" />
        ) : (
          <Icon
            name={step.iconName}
            color={iconColor}
            size={isCurrent ? 'small' : 'tiny'}
          />
        )}
      </Circle>
      <StepLabel $isCurrent={isCurrent} $isCompleted={isCompleted}>
        {step.label}
      </StepLabel>
    </StepContainer>
  );
};

// Styled Components

const StepperContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px 8px 8px;
  width: 100%;
`;

const StepContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 60px;
`;

const Circle = styled.div<{ $isCurrent: boolean; $isCompleted: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${(props) => (props.$isCurrent ? '48px' : '40px')};
  height: ${(props) => (props.$isCurrent ? '48px' : '40px')};
  border-radius: 50%;
  background-color: ${(props) => {
    if (props.$isCurrent) return '#42a5f5'; // Blue for current
    if (props.$isCompleted) return '#66bb6a'; // Green for completed
    return 'rgba(158, 158, 158, 0.2)'; // Grey for upcoming
  }};
  border: 2px solid
    ${(props) => {
      if (props.$isCurrent) return '#42a5f5';
      if (props.$isCompleted) return '#66bb6a';
      return 'rgba(158, 158, 158, 0.3)';
    }};
  transition: all 300ms ease-in-out;
  margin-bottom: 8px;
`;

const StepLabel = styled.span<{ $isCurrent: boolean; $isCompleted: boolean }>`
  font-size: ${(props) => (props.$isCurrent ? '12px' : '10px')};
  font-weight: ${(props) => (props.$isCurrent ? 'bold' : 'normal')};
  color: ${(props) => {
    if (props.$isCurrent) return '#42a5f5';
    if (props.$isCompleted) return '#66bb6a';
    return '#9e9e9e';
  }};
  text-align: center;
  transition: all 300ms ease-in-out;
`;

const ConnectorLine = styled.div<{ isCompleted: boolean }>`
  flex: 1;
  height: 2px;
  background-color: ${(props) =>
    props.isCompleted ? '#66bb6a' : 'rgba(158, 158, 158, 0.3)'};
  margin: 0 4px 22px 4px;
  transition: background-color 300ms ease-in-out;
`;
